import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Share2, 
  Mail, 
  User, 
  Link as LinkIcon, 
  Copy, 
  Check,
  CheckCircle,
  Calendar,
  Shield,
  ExternalLink,
  AlertCircle,
  History,
  XCircle,
  Eye,
  Loader2
} from 'lucide-react';
import { sharesAPI } from '../../services/api';
import { toast } from 'sonner';

const ShareInvoiceModal = ({ isOpen, onClose, invoice, onShareComplete }) => {
  const [step, setStep] = useState('form'); // form, success, history
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shares, setShares] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form state
  const [accountantEmail, setAccountantEmail] = useState('');
  const [accountantName, setAccountantName] = useState('');
  const [message, setMessage] = useState('');
  
  // Result state
  const [shareResult, setShareResult] = useState(null);

  useEffect(() => {
    if (isOpen && invoice) {
      // Reset form when opening
      setStep('form');
      setAccountantEmail('');
      setAccountantName('');
      setMessage('');
      setShareResult(null);
      setCopied(false);
      // Load existing shares for this invoice
      loadInvoiceShares();
    }
  }, [isOpen, invoice?.id]);

  const loadInvoiceShares = async () => {
    if (!invoice?.id) return;
    setLoadingHistory(true);
    try {
      const response = await sharesAPI.getInvoiceShares(invoice.id);
      setShares(response.data);
    } catch (error) {
      console.error('Failed to load shares:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accountantEmail.trim()) {
      toast.error('Veuillez saisir l\'email de votre expert-comptable');
      return;
    }

    setLoading(true);
    try {
      const response = await sharesAPI.create({
        invoice_id: invoice.id,
        accountant_email: accountantEmail.trim(),
        accountant_name: accountantName.trim() || null,
        message: message.trim() || null
      });
      
      setShareResult(response.data);
      setStep('success');
      toast.success('Lien de partage créé et envoyé par email !');
      
      if (onShareComplete) {
        onShareComplete(response.data);
      }
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Erreur lors de la création du partage');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (shareResult?.share_url) {
      navigator.clipboard.writeText(shareResult.share_url);
      setCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevokeShare = async (shareId) => {
    try {
      await sharesAPI.revoke(shareId);
      toast.success('Partage révoqué');
      loadInvoiceShares();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>;
      case 'revoked':
        return <Badge className="bg-red-100 text-red-700">Révoqué</Badge>;
      case 'expired':
        return <Badge className="bg-slate-100 text-slate-700">Expiré</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-50">
              <Share2 className="h-5 w-5 text-blue-600" />
            </div>
            <span>Partager avec votre expert-comptable</span>
          </DialogTitle>
          <DialogDescription>
            {invoice?.invoice_number && (
              <span className="text-slate-500">
                Facture {invoice.invoice_number} • {invoice.grand_total?.toFixed(2)} €
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-4">
          <button
            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
              step === 'form' || step === 'success'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setStep('form')}
          >
            <Share2 className="h-4 w-4 inline mr-2" />
            Nouveau partage
          </button>
          <button
            className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
              step === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setStep('history')}
          >
            <History className="h-4 w-4 inline mr-2" />
            Historique ({shares.length})
          </button>
        </div>

        {/* Form Step */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="accountant-email" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                Email de l'expert-comptable *
              </Label>
              <Input
                id="accountant-email"
                type="email"
                placeholder="comptable@cabinet.fr"
                value={accountantEmail}
                onChange={(e) => setAccountantEmail(e.target.value)}
                required
                data-testid="accountant-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountant-name" className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-400" />
                Nom (optionnel)
              </Label>
              <Input
                id="accountant-name"
                type="text"
                placeholder="M. Dupont"
                value={accountantName}
                onChange={(e) => setAccountantName(e.target.value)}
                data-testid="accountant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-slate-600">
                Message personnalisé (optionnel)
              </Label>
              <Textarea
                id="message"
                placeholder="Bonjour, voici la facture pour le mois de..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                data-testid="share-message-input"
              />
            </div>

            {/* Security Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Lien sécurisé</p>
                  <ul className="mt-1 space-y-1 text-amber-700">
                    <li>• Accès lecture seule uniquement</li>
                    <li>• Expire automatiquement après 30 jours</li>
                    <li>• Révocable à tout moment</li>
                    <li>• Export PDF/CSV autorisé</li>
                  </ul>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="share-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-2" />
                    Créer le lien et envoyer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Success Step */}
        {step === 'success' && shareResult && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Lien de partage créé !
              </h3>
              <p className="text-slate-500 mt-1">
                Un email a été envoyé à <strong>{shareResult.accountant_email}</strong>
              </p>
            </div>

            {/* Share Link */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-600">Lien de partage</Label>
                <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
              </div>
              <div className="flex gap-2">
                <Input
                  value={shareResult.share_url}
                  readOnly
                  className="bg-white text-sm"
                  data-testid="share-url-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="flex-shrink-0"
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Expiration Info */}
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="h-4 w-4" />
              <span>
                Expire le {formatDate(shareResult.expires_at)}
              </span>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(shareResult.share_url, '_blank')}
                className="flex-1"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Aperçu
              </Button>
              <Button
                onClick={() => setStep('form')}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Nouveau partage
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* History Step */}
        {step === 'history' && (
          <div className="space-y-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : shares.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Share2 className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-slate-600">Aucun partage pour cette facture</p>
                <Button
                  variant="outline"
                  onClick={() => setStep('form')}
                  className="mt-4"
                >
                  Créer un premier partage
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {shares.map((share) => (
                  <div
                    key={share.id}
                    className={`border rounded-lg p-3 transition-colors ${
                      share.owner_notified 
                        ? 'border-emerald-200 bg-emerald-50/50' 
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`share-item-${share.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-900">
                            {share.accountant_email}
                          </span>
                          {getStatusBadge(share.status)}
                          {share.owner_notified && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              <Eye className="h-3 w-3 mr-1" />
                              Consulté
                            </Badge>
                          )}
                        </div>
                        {share.accountant_name && (
                          <p className="text-sm text-slate-500 ml-6">
                            {share.accountant_name}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 ml-6 text-xs text-slate-400">
                          <span>
                            Créé le {formatDate(share.created_at)}
                          </span>
                          {share.first_accessed_at && (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="h-3 w-3" />
                              1ère consultation : {formatDate(share.first_accessed_at)}
                            </span>
                          )}
                          {share.accessed_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {share.accessed_count} consultation{share.accessed_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {share.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeShare(share.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`revoke-btn-${share.id}`}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareInvoiceModal;
